import React, { useState, useCallback } from 'react';
import { Card, Col, Row, Tag, Typography, Button, Space, Spin } from 'antd';
import {
  DatabaseOutlined,
  ThunderboltOutlined,
  CloudOutlined,
  ApiOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  MinusCircleOutlined,
} from '@ant-design/icons';
import { apiService } from '../../services/api';

const { Title, Text } = Typography;

interface ServiceResult {
  status: 'ok' | 'error' | 'not_configured';
  url?: string;
  error?: string;
  bucket?: string;
  project?: string;
  workers?: string[];
  detail?: string;
}

interface StatusData {
  database?: ServiceResult;
  redis?: ServiceResult;
  celery?: ServiceResult;
  gcs?: ServiceResult;
}

const StatusTag: React.FC<{ status: ServiceResult['status'] }> = ({ status }) => {
  if (status === 'ok') {
    return (
      <Tag icon={<CheckCircleOutlined />} color="success">
        Online
      </Tag>
    );
  }
  if (status === 'error') {
    return (
      <Tag icon={<CloseCircleOutlined />} color="error">
        Offline
      </Tag>
    );
  }
  return (
    <Tag icon={<MinusCircleOutlined />} color="default">
      Not Configured
    </Tag>
  );
};

interface ServiceCardProps {
  title: string;
  icon: React.ReactNode;
  service?: ServiceResult;
  detail: React.ReactNode;
}

const ServiceCard: React.FC<ServiceCardProps> = ({ title, icon, service, detail }) => (
  <Card
    style={{ height: '100%' }}
    styles={{ body: { height: '100%' } }}
  >
    <Space direction="vertical" size="small" style={{ width: '100%' }}>
      <Space>
        <span style={{ fontSize: 20, color: '#722ed1' }}>{icon}</span>
        <Text strong style={{ fontSize: 16 }}>
          {title}
        </Text>
        {service && <StatusTag status={service.status} />}
      </Space>
      <div style={{ fontFamily: 'monospace', fontSize: 12, color: '#595959' }}>
        {detail}
      </div>
      {service?.error && (
        <Text type="danger" style={{ fontSize: 12 }}>
          {service.error}
        </Text>
      )}
      {service?.detail && service.status === 'error' && (
        <Text type="danger" style={{ fontSize: 12 }}>
          {service.detail}
        </Text>
      )}
      {service?.workers && (
        <Text type="secondary" style={{ fontSize: 12 }}>
          Workers: {service.workers.join(', ')}
        </Text>
      )}
    </Space>
  </Card>
);

const ServiceStatus: React.FC = () => {
  const [status, setStatus] = useState<StatusData | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiService.getServiceStatus();
      setStatus(data);
    } catch (err) {
      console.error('Failed to fetch service status:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  return (
    <div>
      <Space style={{ marginBottom: 24, justifyContent: 'space-between', width: '100%' }}>
        <Title level={3} style={{ margin: 0 }}>
          Service Status
        </Title>
        <Button
          icon={<ReloadOutlined spin={loading} />}
          onClick={fetchStatus}
          loading={loading}
        >
          Refresh
        </Button>
      </Space>

      <Spin spinning={loading && !status}>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <ServiceCard
              title="Database"
              icon={<DatabaseOutlined />}
              service={status?.database}
              detail={status?.database?.url ?? '—'}
            />
          </Col>
          <Col xs={24} md={12}>
            <ServiceCard
              title="Redis"
              icon={<ThunderboltOutlined />}
              service={status?.redis}
              detail={status?.redis?.url ?? '—'}
            />
          </Col>
          <Col xs={24} md={12}>
            <ServiceCard
              title="Celery"
              icon={<ApiOutlined />}
              service={status?.celery}
              detail={
                status?.celery?.workers?.length
                  ? `${status.celery.workers.length} worker(s) online`
                  : status?.celery?.detail ?? '—'
              }
            />
          </Col>
          <Col xs={24} md={12}>
            <ServiceCard
              title="Google Cloud Storage"
              icon={<CloudOutlined />}
              service={status?.gcs}
              detail={
                status?.gcs ? (
                  <>
                    {status.gcs.bucket && <div>Bucket: {status.gcs.bucket}</div>}
                    {status.gcs.project && <div>Project: {status.gcs.project}</div>}
                    {!status.gcs.bucket && !status.gcs.project && '—'}
                  </>
                ) : (
                  '—'
                )
              }
            />
          </Col>
        </Row>
      </Spin>
    </div>
  );
};

export default ServiceStatus;
